// BR-38 — what the Cierres screens offer the signed-in person, from `/access/me`.
//
// The backend is the authority (BR-35 scopes every read, BR-36 asks every action its permission),
// and nothing here can make it accept something it would refuse. What this file decides is what a
// screen OFFERS: a button the server will answer with a 403 reads as a broken screen, so a person
// who may not reopen a closing is not shown "Reabrir cierre".
//
// **The screens follow the permissions only when the server does: `enforced`.** Under `off` the
// server serves every action to every person of the business, and under `shadow` it serves them
// and records what it would have refused — and that record is the whole point of the shadow week
// (BR-41): a button hidden here is an attempt that never reaches the server, so it would never
// show up in the report Brinks reviews before going to `enforced`. `MODOS_DE_ACCESO.shadow` says
// the same to the administrator: "cada persona sigue viendo lo de hoy".
//
// `acceso` is `/access/me`'s answer as the shell keeps it in the auth state:
//   undefined   not known yet — nothing gated is offered until it is
//   null        the request failed — today's screens; the server still decides every action
//   { mode, scopes, … }
//
// A target is where an action would land:
//   undefined                         anywhere at all
//   { todos: true }                   every client of the business, present and future
//   { cliente }                       somewhere in that client
//   { cliente, entero: true }         the whole client, no region or office narrowing
//   { cliente, oficina, region }      that office
// The containment is BR-37's `incluye`, which mirrors the backend's `includesOffice`: an office is
// inside a region grant only by its own `region`, so an office whose region the caller does not
// pass is judged by the client and office grants alone.

import { sinPermiso } from "./accesoDiccionario";
import { alcanceDe, incluye } from "./cierreAcceso";

export const PERMISO = Object.freeze({
  VER_CIERRES: "cierres.closings.view",
  VER_DOCUMENTOS: "cierres.documents.view",
  VER_REPORTES: "cierres.reports.view",
  EXPORTAR: "cierres.export",
  USAR_ASISTENTE: "cierres.chat.use",
  CREAR_CIERRES: "cierres.closings.create",
  CARGAR_DOCUMENTOS: "cierres.documents.upload",
  CORREGIR_VALORES: "cierres.datapoints.edit",
  EVALUAR: "cierres.closings.evaluate",
  ATENDER_ALERTAS: "cierres.alerts.resolve",
  REABRIR: "cierres.closings.reopen",
  PROCESAR_DIA: "cierres.daily.run",
  ADMINISTRAR_CALENDARIO: "cierres.calendar.manage",
  // B-89: the addresses each office's own report goes to, per office.
  ADMINISTRAR_CORREOS_OFICINA: "cierres.offices.contacts",
  // B-134: an office's GLORY switch, per office.
  CONFIGURAR_OFICINAS: "cierres.offices.configure",
  // B-92: who gets the client's alert emails, and which alerts are urgent — on the whole client.
  CONFIGURAR_ALERTAS: "cierres.notifications.manage",
  // B-139: the Equipo tab of Reportes — every person's work on the client, so on the whole client.
  VER_EQUIPO: "cierres.team.view",
  ADMINISTRAR_USUARIOS: "access.users.manage",
});

export const sigueLosPermisos = (acceso) => Boolean(acceso) && acceso.mode === "enforced";

const esVacio = (alcance) => !alcance || (!alcance.all && (alcance.clients || []).length === 0);

export function puede(acceso, permiso, objetivo) {
  if (acceso === undefined) return false;
  if (!sigueLosPermisos(acceso)) return true;

  const alcance = alcanceDe(acceso, permiso);
  if (!objetivo) return !esVacio(alcance);
  if (objetivo.todos) return alcance.all === true;
  // A screen that does not know the client (a list that failed to load, on a business with one
  // client) asks about the permission at all; the server resolves the only client and decides.
  if (!objetivo.cliente) return !esVacio(alcance);
  if (objetivo.entero) return incluye(alcance, { tipo: "cliente", cliente: objetivo.cliente });
  if (objetivo.oficina) {
    return incluye(
      alcance,
      { tipo: "oficina", cliente: objetivo.cliente, oficina: objetivo.oficina },
      () => objetivo.region
    );
  }
  return alcance.all === true || (alcance.clients || []).some((c) => c.code === objetivo.cliente);
}

// --- navigation ---------------------------------------------------------------------------------

// Each nav item of the shell, by its `enabledSections` key: the item shows when the section is
// enabled for the business AND this says yes. Alertas is a view of the closings' alerts, so it
// goes with the closings. Configuración is a disabled placeholder for another product line and
// asks nothing.
//
// The assistant needs `cierres.chat.use` anywhere. Since BR-39 it answers each person within their
// own view — the server applies their scope to every tool — so a person who may see one client is
// answered about that client. Until then it needed the permission on EVERY client, because the
// server refused anyone narrower (`ASSISTANT_NOT_AVAILABLE`, now gone).
const REGLA_DE_SECCION = {
  CIERRES_PANEL: (acceso) => puede(acceso, PERMISO.VER_CIERRES),
  CIERRES_CALENDARIO: (acceso) => puede(acceso, PERMISO.VER_CIERRES),
  CIERRES_OFICINAS: (acceso) => puede(acceso, PERMISO.VER_CIERRES),
  CIERRES_ALERTAS: (acceso) => puede(acceso, PERMISO.VER_CIERRES),
  CIERRES_REPORTES: (acceso) => puede(acceso, PERMISO.VER_REPORTES),
  CIERRES_ASISTENTE: (acceso) => puede(acceso, PERMISO.USAR_ASISTENTE),
  CIERRES_USUARIOS: (acceso) => puede(acceso, PERMISO.ADMINISTRAR_USUARIOS),
};

export const seccionPermitida = (acceso, seccion) =>
  REGLA_DE_SECCION[seccion] ? REGLA_DE_SECCION[seccion](acceso) : true;

// What the page says in place of a section the person may not see — reached by typing its
// address, or by the redirect every cierres business lands on (the panel).
const PERMISO_DE_SECCION = {
  CIERRES_PANEL: PERMISO.VER_CIERRES,
  CIERRES_CALENDARIO: PERMISO.VER_CIERRES,
  CIERRES_OFICINAS: PERMISO.VER_CIERRES,
  CIERRES_ALERTAS: PERMISO.VER_CIERRES,
  CIERRES_REPORTES: PERMISO.VER_REPORTES,
  CIERRES_ASISTENTE: PERMISO.USAR_ASISTENTE,
  CIERRES_USUARIOS: PERMISO.ADMINISTRAR_USUARIOS,
};

export const sinSeccion = (acceso, seccion) => sinPermiso(PERMISO_DE_SECCION[seccion]);

// --- one closing --------------------------------------------------------------------------------

// Where a closing lands, from what a screen has: the closing's own client and office when the
// detail has loaded, the panel row's otherwise. The region only ever comes from a panel row or an
// office list — the detail does not carry it.
export const objetivoDeCierre = ({ cliente, oficina, region } = {}) => ({
  cliente: cliente || "",
  oficina: oficina || "",
  region: typeof region === "string" && region ? region : undefined,
});

// What a person may do to one closing, each asked of its office.
export const accionesDeCierre = (acceso, objetivo) => ({
  verDocumentos: puede(acceso, PERMISO.VER_DOCUMENTOS, objetivo),
  cargar: puede(acceso, PERMISO.CARGAR_DOCUMENTOS, objetivo),
  corregir: puede(acceso, PERMISO.CORREGIR_VALORES, objetivo),
  evaluar: puede(acceso, PERMISO.EVALUAR, objetivo),
  atender: puede(acceso, PERMISO.ATENDER_ALERTAS, objetivo),
  reabrir: puede(acceso, PERMISO.REABRIR, objetivo),
  // B-80 — redoing is a reopen with more thrown away: the same permission.
  rehacer: puede(acceso, PERMISO.REABRIR, objetivo),
});

// "Trabajar a mano" opens a dialog of four actions; with none of them it would be a dialog of
// disabled buttons.
export const puedeTrabajarAMano = (acciones) =>
  acciones.cargar || acciones.corregir || acciones.evaluar || acciones.reabrir;

// --- export -------------------------------------------------------------------------------------

// The exports are built in the browser from rows already on screen, so no route on the server can
// refuse one — BR-35 left hiding them to this task. A person may hold `cierres.export` on a
// narrower scope than they see (Auditor of one region, Operador of the whole client), so what is
// written is the rows whose office the export reaches, never everything on screen.
//
// `oficinaDe(fila)` gives `{ oficina, region }` for one row of that screen.
export const filasExportables = (acceso, cliente, filas, oficinaDe) =>
  sigueLosPermisos(acceso)
    ? (filas || []).filter((fila) => puede(acceso, PERMISO.EXPORTAR, { cliente, ...oficinaDe(fila) }))
    : filas || [];

// --- the assistant ------------------------------------------------------------------------------

// The assistant's refusal (`ASSISTANT_FORBIDDEN`, `cierresChatAccess.js`) is written for the
// person; every other failure keeps the generic line the chat has always shown.
export const errorDelAsistente = (response, generico) =>
  typeof response?.errorCode === "string" && response.errorCode.startsWith("ASSISTANT_") && response.message
    ? response.message
    : generico;
